import { useAuthStore } from '@/stores/auth'
import { useTeamsStore } from '@/stores/teams'
import { useNotificationStore } from '@/stores/notifications'

export async function requireAuth(to, from, next) {
  const authStore = useAuthStore()

  try {
    await authStore.init()
    
    if (authStore.isAuthenticated) {
      // Benachrichtigungen initialisieren wenn Benutzer eingeloggt ist
      const notificationStore = useNotificationStore()
      if (!notificationStore.isPolling) {
        await notificationStore.initializeNotifications()
      }
      next()
    } else {
      next({ name: 'login', query: { redirect: to.fullPath } })
    }
  } catch (error) {
    console.error('Auth guard error:', error)
    next({ name: 'login', query: { redirect: to.fullPath } })
  }
}

export async function requireSuperAdmin(to, from, next) {
  const authStore = useAuthStore()

  try {
    await authStore.init()
    
    if (authStore.isImpersonated()) {
      next({ name: 'dashboard' })
      return
    }

    if (authStore.isAuthenticated && authStore.isSuperAdmin) {
      next()
    } else if (authStore.isAuthenticated && !authStore.isSuperAdmin) {
      next({ name: 'dashboard' })
    } else {
      next({ name: 'login', query: { redirect: to.fullPath } })
    }
  } catch (error) {
    console.error('Super admin guard error:', error)
    next({ name: 'login', query: { redirect: to.fullPath } })
  }
}

export async function requireAuthWithInvitation(to, from, next) {
  const authStore = useAuthStore()

  try {
    await authStore.init()
    
    if (authStore.isAuthenticated) {
      next()
    } else {
      if (to.params.code) {
        authStore.setPendingInvitationCode(to.params.code)
      }
      next({ name: 'login', query: { redirect: to.fullPath } })
    }
  } catch (error) {
    console.error('Auth guard error:', error)
    next({ name: 'login', query: { redirect: to.fullPath } })
  }
}

export async function requireGuest(to, from, next) {
  const authStore = useAuthStore()

  try {
    await authStore.init()

    if (authStore.isAuthenticated) {
      next({ name: 'dashboard' })
    } else {
      next()
    }
  } catch (error) {
    console.error('Guest guard error:', error)
    next()
  }
}

export async function requireProTeam(to, from, next) {
  const authStore = useAuthStore()
  const teamsStore = useTeamsStore()

  try {
    await authStore.init()
    
    if (!authStore.isAuthenticated) {
      next({ name: 'login', query: { redirect: to.fullPath } })
      return
    }

    const teamId = to.params.id
    if (!teamId) {
      next({ name: 'teams-list' })
      return
    }

    let team = teamsStore.selectedTeam
    if (!team || team.id !== teamId) {
      try {
        team = await teamsStore.loadTeamById(teamId)
      } catch (error) {
        console.error('Error loading team:', error)
        next({ name: 'teams-list' })
      }
    }

    if (!team) {
      next({ name: 'teams-list' })
      return
    }

    if (team.status === 'pro' || team.status === 'ultimate') {
      next()
    } else {
      next({
        name: 'teams-detail',
        params: { id: teamId },
        query: {
          error: 'customization_requires_pro',
          from: 'customization',
        },
      })
    }
  } catch (error) {
    console.error('Pro team guard error:', error)
    next({ name: 'teams-list' })
  }
}

export async function requireRefundData(to, from, next) {
  const authStore = useAuthStore()
  const teamsStore = useTeamsStore()

  try {
    await authStore.init()
    
    if (!authStore.isAuthenticated) {
      next({ name: 'login', query: { redirect: to.fullPath } })
      return
    }

    const teamId = to.params.id
    if (!teamId) {
      next({ name: 'teams-list' })
      return
    }

    let team = teamsStore.selectedTeam
    if (!team || team.id !== teamId) {
      try {
        team = await teamsStore.loadTeamById(teamId)
      } catch (error) {
        console.error('Error loading team:', error)
        next({ name: 'teams-list' })
        return
      }
    }

    if (!team) {
      next({ name: 'teams-list' })
      return
    }

    if (team.status !== 'pro' && team.status !== 'ultimate') {
      next()
      return
    }

    const { teamService } = await import('@/api/teams')

    try {
      const refundSettings = await teamService.getTeamRefundSettings(teamId)

      if (!refundSettings.enabled) {
        next()
        return
      }

      const hasAvailableMethods =
        refundSettings.paypalEnabled ||
        refundSettings.bankTransferEnabled ||
        refundSettings.cashEnabled

      if (!hasAvailableMethods) {
        next()
        return
      }

      const currentData = await teamService.getUserRefundData(teamId)

      if (currentData && currentData.refundMethod) {
        let isValid = true

        if (currentData.refundMethod === 'paypal' && !currentData.paypalMeName) {
          isValid = false
        } else if (currentData.refundMethod === 'bank_transfer') {
          if (!currentData.iban || !currentData.bic || !currentData.accountHolder) {
            isValid = false
          }
        }

        if (isValid) {
          next() 
          return
        }
      }

      next({
        name: 'teams-refund',
        params: { id: teamId },
        query: {
          redirect: to.fullPath,
          requireSetup: 'true',
        },
      })
    } catch (error) {
      console.error('Error checking refund data:', error)

      next()
    }
  } catch (error) {
    console.error('Refund data guard error:', error)
    next({ name: 'teams-list' })
  }
}

export async function requireReceiptAccess(to, from, next) {
  const authStore = useAuthStore()

  try {
    await authStore.init()
    
    if (authStore.isAuthenticated) {
      next()
      return
    }

    
    const receiptId = to.params.id
    const authKey = localStorage.getItem(`receipt_auth_${receiptId}`)
    
    if (authKey) {
      
      next()
      return
    }

    
    next({
      name: 'receipt-access',
      params: { id: to.params.id },
      query: { redirect: to.fullPath },
    })
  } catch (error) {
    console.error('Receipt access guard error:', error)
    next({
      name: 'receipt-access',
      params: { id: to.params.id },
      query: { redirect: to.fullPath },
    })
  }
}

export async function requireMaintenanceMode(to, from, next) {
  const authStore = useAuthStore()

  try {
    await authStore.init()
    
    // Check if user is authenticated
    if (!authStore.isAuthenticated) {
      next({ name: 'login', query: { redirect: to.fullPath } })
      return
    }

    // Check if user is super admin
    if (authStore.isSuperAdmin) {
      // Super admins can access maintenance page even if maintenance mode is not active
      next()
      return
    }

    // Check maintenance mode status
    const maintenanceService = (await import('@/services/maintenanceService')).default
    const maintenanceStatus = await maintenanceService.checkMaintenanceMode()
    
    if (maintenanceStatus.isMaintenanceMode) {
      // Maintenance mode is active, allow access to maintenance page
      next()
    } else {
      // Maintenance mode is not active, redirect to dashboard
      next({ name: 'dashboard' })
    }
  } catch (error) {
    console.error('Maintenance mode guard error:', error)
    // On error, redirect to dashboard
    next({ name: 'dashboard' })
  }
}
